"use client"

import { useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import { toast } from "sonner"
import { MessageSquareWarning } from "lucide-react"
import {
  getDiagnosticsSnapshot,
  initDiagnosticsBuffer,
} from "@/lib/diagnostics-buffer"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

/**
 * App-wide "Report a Problem" button. Any signed-in staff member can describe
 * something that's wrong; the screen they're on plus a rolling buffer of
 * recent console errors / failed requests are captured automatically. The
 * report is emailed to the operations inbox AND stored in the
 * `problem_reports` collection (see `app/api/report-problem/route.ts`).
 * Intentionally tiny — this is the staff side of the fix loop for the
 * section-by-section production rollout, not a ticketing system.
 */
export function ReportProblemButton() {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const [description, setDescription] = useState("")
  const [submitting, setSubmitting] = useState(false)

  // Start the rolling diagnostics buffer as early as possible — this component
  // mounts in the root layout on every authenticated page, so console errors /
  // failed requests are already recorded by the time someone files a report.
  useEffect(() => {
    initDiagnosticsBuffer()
  }, [])

  // Public pre-login screens have no staff context — don't show it there.
  if (pathname === "/" || pathname === "/login" || pathname === "/landing") {
    return null
  }

  async function submit() {
    const text = description.trim()
    if (!text) {
      toast.error("Please describe the problem first.")
      return
    }
    setSubmitting(true)
    try {
      const res = await fetch("/api/report-problem", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          description: text,
          route: pathname,
          // Recent console errors / failed requests, captured automatically —
          // staff don't have to (and can't) copy-paste dev tools on a tablet.
          diagnostics: getDiagnosticsSnapshot(),
        }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean
        error?: string
      }
      if (res.ok && data.success) {
        toast.success("Thanks — your report was sent. We'll look into it.")
        setDescription("")
        setOpen(false)
      } else {
        toast.error(data.error || "Could not send the report. Please tell a manager.")
      }
    } catch {
      toast.error("Network problem — could not send. Please tell a manager.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="fixed bottom-4 left-4 z-30 bg-white/90 shadow-md print:hidden h-11 w-11 rounded-full p-0 opacity-80 hover:opacity-100 transition-opacity sm:left-auto sm:right-4 sm:h-9 sm:w-auto sm:rounded-md sm:px-3 sm:gap-2 sm:opacity-100"
          aria-label="Report a problem with the app"
          title="Report a problem"
        >
          <MessageSquareWarning className="h-5 w-5 sm:h-4 sm:w-4 text-warning" />
          <span className="hidden sm:inline">Report a problem</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Report a problem</DialogTitle>
          <DialogDescription>
            Describe what went wrong and what you expected. We automatically
            include which screen you were on so we can find it fast.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What happened? What did you expect instead?"
          rows={6}
          maxLength={4000}
          autoFocus
          disabled={submitting}
        />
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? "Sending…" : "Send report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
